// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {getProjectId, getReportUrl, getWorkspaceId} from 'common/report/util';
import {cohortsClient} from 'common/report/queries/app-api/cohorts-client';
import {addToast} from 'common/components/toast-area/toast-manager';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {Toast} from 'common/components/toast-area';
import {buildCreateEntityModalAttrs, normalizeCohortEntity} from 'common/report/util/saved-entities';
import {currentProjectStore} from 'common/report/stores/project-details';
import {FeatureGate} from 'common/report/constants';
import {emitTrackEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {CohortType as FlowsCohortType} from 'common/report/flows/models/types';
import {CohortType as FunnelCohortType} from 'common/report/funnels/models/types';
import {CohortType as RetentionCohortType} from 'common/report/retention/models/types';
import {cohortsStore} from 'common/report/stores/cohorts';
import {useStore} from 'common/util/component-helpers';
import {ButtonLayout, ButtonTheme} from 'common/components/button/constants';
import {SelectTheme} from 'common/components/select/constants';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {IronContextRegistry} from 'common/report/context-registry';

import {promptSharing} from '../sharing';
import {createEntityModalPromise} from '../create-entity-modal';
import {ModalReturn} from '../create-entity-modal/types';
import {getUpsellInfo, ModalTrigger} from '../in-product-upsell-modal/util';
import {promptInProductUpsellModal} from '../in-product-upsell-modal/prompt';
import {template} from './template';
import css from './index.inline.styl';

type State = {
  cohortDetails: CohortDetail[];
};
export type CohortActionEvent = CustomEvent<{
  name: string;
  description: string;
  type: CohortDetail[`type`];
}>;

type CreateCohortButtonJsxProps = {
  attrs: {
    block?: boolean;
    tooltip?: string;
    disabled?: boolean;
    label?: string;
    'show-upsell-in-dropdown'?: boolean;
    theme?: ButtonTheme | SelectTheme;
    layout?: ButtonLayout;
    icon?: string;
  };
  props: {
    cohortDetails: CohortDetail[];
  };
  on?: {
    create?: (ev: CohortActionEvent) => void;
    cancel?: (ev: CohortActionEvent) => void;
    save?: (ev: CohortActionEvent) => void;
    saveFail?: (ev: CohortActionEvent) => void;
  };
};

export interface CohortDetail {
  cohort: Cohort;

  /** The type of the cohort */
  type: FlowsCohortType | FunnelCohortType | `insights` | RetentionCohortType;

  /** The warning to be displayed in the modal */
  warning: string;

  /** label for the dropdown to display */
  label: string;
}

export class CreateCohortButton extends Component<
  State,
  CreateCohortButtonJsxProps[`attrs`],
  unknown,
  unknown,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<CreateCohortButtonJsxProps[`attrs`]> {
    return {
      block: {type: `boolean`, description: `Whether to block the button from expanding to full width`},
      'show-upsell-in-dropdown': {
        type: `boolean`,
        description: `Whether to show the upsell in the dropdown`,
      },
      theme: {type: `string`, default: ButtonTheme.Secondary},
      layout: {type: `string`, default: ButtonLayout.Horizontal},
      icon: {type: `string`},
      tooltip: {type: `string`},
      disabled: {type: `boolean`},
      label: {type: `string`},
    };
  }

  get config() {
    return {
      template,
      useShadowDom: true,
      css,
      contexts: [`i18n` as const, `currentProject` as const],
      defaultState: {
        cohortDetails: [],
        disabled: false,
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    useStore(this, cohortsStore);
    cohortsStore.fetch({
      projectId: getProjectId(),
      workspaceId: getWorkspaceId(),
    });
  }

  set cohortDetails(cohortDetails: CohortDetail[]) {
    this.update({
      cohortDetails,
    });
  }

  getCohortNames() {
    return Object.values(cohortsStore.value || {}).map((cohort) => cohort.name);
  }

  checkRenderUpsellModal = (): boolean => {
    return !!getUpsellInfo(
      this.getContext(`currentProject`)?.get()?.value,
      ModalTrigger.COHORT,
      this.getContext(`i18n`),
    );
  };

  handleUpsell = () => {
    if (this.checkRenderUpsellModal()) {
      promptInProductUpsellModal(ModalTrigger.COHORT);
      return;
    }

    emitTrackEvent(this.el, {
      eventName: `Click Upsell`,
      props: {
        'Feature Limit': FeatureGate.RestrictEntitySaving,
        'Entity Type': `cohorts`,
      },
    });
    window.open(`/pricing`, `_blank`).focus();
  };

  handleClick = (ev) => {
    if (this.checkRenderUpsellModal()) {
      promptInProductUpsellModal(ModalTrigger.COHORT);
      return;
    }
    if (ev.detail.item?.value === `upsell`) {
      this.handleUpsell();
      return;
    }
    const cohortDetail =
      this.state.cohortDetails.length === 1
        ? this.state.cohortDetails[0]
        : this.state.cohortDetails.find((detail) => detail.type === ev.detail.item.value);
    const {warning} = cohortDetail;
    this.dispatchEvent(
      new CustomEvent(`create`, {
        composed: true,
        detail: {
          name: cohortDetail.cohort.name,
          description: cohortDetail.cohort.description,
          type: cohortDetail.type,
        },
      }),
    );
    createEntityModalPromise(
      {
        ...buildCreateEntityModalAttrs({
          projectId: parseInt(getProjectId(), 10),
          entityType: EntityType.Cohort,
          modalOpen: true,
          isEditing: false,
          isDuplicating: false,
          entity: normalizeCohortEntity(cohortDetail.cohort),
          showShareManagement: currentProjectStore.value?.getFeatureGate(FeatureGate.Sharing),
          i18n: this.getContext(`i18n`),
        }),
        ...(warning ? {warning} : {}),
      },
      {
        existingEntityNames: this.getCohortNames(),
      },
    ).then((result: ModalReturn) => {
      if (result.didSave) {
        this.handleCreateCohort(result, cohortDetail);
      } else {
        this.handleCancelCohort(result, cohortDetail);
      }
    });
  };

  handleCreateCohort = async (ev: ModalReturn, cohortDetail: CohortDetail) => {
    let toast: Toast;
    const projectId = parseInt(getProjectId(), 10);
    const workspaceId = parseInt(getWorkspaceId(), 10);
    try {
      const newCohort = await cohortsClient.createCohort({
        projectId,
        workspaceId,
        cohort: {
          ...cohortDetail.cohort.serialize(),
          name: ev.name,
          description: ev.description,
        },
      });
      await cohortsStore.refresh();
      this.dispatchEvent(
        new CustomEvent(`save`, {
          composed: true,
          detail: {
            name: ev.name,
            description: ev.description,
            type: cohortDetail.type,
          },
        }),
      );
      const undoCallback = async () => {
        try {
          await cohortsClient.deleteCohortById({
            projectId,
            cohortID: newCohort.id,
          });
          addToast({
            message: `Cohort ${ev.name} was successfully deleted.`,
          });
          await cohortsStore.refresh();
        } catch (error) {
          this.dispatchEvent(
            new CustomEvent(`saveFail`, {
              composed: true,
              detail: {
                name: ev.name,
                description: ev.description,
                type: cohortDetail.type,
              },
            }),
          );
          addToast({
            isError: true,
            message: `Your cohort could not be deleted`,
          });
          throw error;
        }
      };
      const navToCohortUserViewFunction = () => {
        window.open(
          getReportUrl(`explore`, {projectId, workspaceId}) +
            Cohort.getExploreHashForUsersInCohort({
              cohortId: newCohort.id,
              cohortName: newCohort.name,
              dataGroupId: newCohort.data_group_id,
            }),
        );
      };

      toast = {
        isError: false,
        message: `Cohort created`,
        cta: `Open`,
        ctaCallback: navToCohortUserViewFunction,
        undoable: true,
        undoCallback,
      };
      if (ev.event === `saveAndShare`) {
        await promptSharing({
          type: SharableEntityType.Cohort,
          id: newCohort.id,
          'project-id': projectId,
        });
      }
    } catch (error) {
      toast = {
        isError: true,
        message: `There was an error creating your cohort`,
      };
      throw error;
    } finally {
      addToast(toast);
    }
  };

  handleCancelCohort = (ev: ModalReturn, cohortDetail: CohortDetail) => {
    this.dispatchEvent(
      new CustomEvent(`cancel`, {
        composed: true,
        detail: {
          name: ev.name,
          description: ev.description,
          type: cohortDetail.type,
        },
      }),
    );
  };
}

export const CreateCohortButtonEl = defineAndMakeJsxFactory<CreateCohortButtonJsxProps>(
  `mp-create-cohort-button`,
  CreateCohortButton,
);
export const CreateCohortButtonReact = panel2react<CreateCohortButtonJsxProps>(
  `mp-create-cohort-button`,
  CreateCohortButton,
);
